import { useState, useCallback, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { requestMobile } from '@/lib/request'
import { toast } from 'sonner'

const TRONGRID_API_KEY = 'b6087184-322e-42cd-9c61-b08e4f92d4cb'
const TRONGRID_USDT_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'
const TRONGRID_POLL_INTERVAL = 8000
const TRONGRID_LOOKBACK_MS = 5 * 60 * 1000

interface RechargeType {
  id: string
  name: string
  type?: string | number
  tex1?: string
  tex2?: string
}

interface ChannelInfo {
  id: string
  quickAmount?: string
}

interface UsdtInfo {
  id: string
  chain_name: string
  channel_name: string
  recharge_address: string
}

interface TronGridTrc20Transaction {
  transaction_id: string
  to?: string
  value?: string
  block_timestamp?: number
  token_info?: {
    address?: string
    decimals?: number
  }
}

type UsdtTransactionStatus = 'pending' | 'confirmed'

interface MatchedUsdtTransaction {
  transactionId: string
  amount: string
  blockTimestamp?: number
  status: UsdtTransactionStatus
}

interface RecentUsdtTransaction {
  transactionId: string
  amount: string
  blockTimestamp?: number
  status: UsdtTransactionStatus
}

interface RechargeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountInfo: {
    plat: string
    remarks_name: string
  }
  onSuccess?: () => void
}

const normalizeAddress = (address?: string) => (address || '').trim()

const normalizeAmount = (value: string) => value.trim().replace(/,/g, '')

const decimalToUnits = (value: string, decimals: number) => {
  const normalized = normalizeAmount(value)
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null

  const [integerPart, fractionPart = ''] = normalized.split('.')
  if (fractionPart.length > decimals) return null

  const paddedFraction = fractionPart.padEnd(decimals, '0')
  return BigInt(`${integerPart}${paddedFraction}`.replace(/^0+(?=\d)/, '') || '0')
}

const isPositiveAmount = (value: string) => {
  const units = decimalToUnits(value, 6)
  return units !== null && units > 0n
}

const formatMonitorTime = (timestamp?: number) =>
  timestamp ? new Date(timestamp).toLocaleTimeString('zh-CN') : ''

const formatTransactionStatus = (status: UsdtTransactionStatus) =>
  status === 'confirmed' ? '已确认' : '待确认'

const formatTransactionId = (transactionId: string) =>
  transactionId.length > 16
    ? `${transactionId.slice(0, 8)}...${transactionId.slice(-8)}`
    : transactionId

const unitsToDecimal = (value: string, decimals: number) => {
  const units = BigInt(value)
  const base = 10n ** BigInt(decimals)
  const integerPart = units / base
  const fractionPart = units % base
  const fractionText = fractionPart.toString().padStart(decimals, '0').replace(/0+$/, '')
  return fractionText ? `${integerPart}.${fractionText}` : integerPart.toString()
}

const isUsdtRechargeType = (type: RechargeType) => type.name.indexOf('USDT') > -1

// 名称含 USDT 且 type === 1：走正常充值逻辑（渠道 + 支付链接）
const isNormalUsdtRechargeType = (type: RechargeType) =>
  isUsdtRechargeType(type) && String(type.type) === '1'

const isSupportedUsdtRechargeType = (type: RechargeType) =>
  !isUsdtRechargeType(type) || isNormalUsdtRechargeType(type) || String(type.type) === '4'

const fetchTronGridUsdtTransactions = async (
  address: string,
  signal: AbortSignal,
  options: {
    limit: number
    minTimestamp?: number
    status: UsdtTransactionStatus
  },
): Promise<TronGridTrc20Transaction[]> => {
  const params = new URLSearchParams({
    limit: String(options.limit),
    contract_address: TRONGRID_USDT_CONTRACT,
    order_by: 'block_timestamp,desc',
  })

  if (options.status === 'confirmed') {
    params.set('only_confirmed', 'true')
  } else {
    params.set('only_unconfirmed', 'true')
  }

  if (options.minTimestamp) {
    params.set('min_timestamp', String(options.minTimestamp))
  }

  const response = await fetch(
    `https://api.trongrid.io/v1/accounts/${encodeURIComponent(address)}/transactions/trc20?${params.toString()}`,
    {
      headers: {
        'TRON-PRO-API-KEY': TRONGRID_API_KEY,
      },
      signal,
    },
  )

  if (!response.ok) {
    throw new Error(`TronGrid HTTP ${response.status}`)
  }

  const result = await response.json()
  const transactions: TronGridTrc20Transaction[] = Array.isArray(result.data)
    ? result.data
    : []

  return transactions
}

const findMatchingUsdtTransaction = async (
  address: string,
  amount: string,
  signal: AbortSignal,
): Promise<MatchedUsdtTransaction | null> => {
  const minTimestamp = Date.now() - TRONGRID_LOOKBACK_MS
  const [pendingTransactions, confirmedTransactions] = await Promise.all([
    fetchTronGridUsdtTransactions(address, signal, {
      limit: 50,
      minTimestamp,
      status: 'pending',
    }),
    fetchTronGridUsdtTransactions(address, signal, {
      limit: 50,
      minTimestamp,
      status: 'confirmed',
    }),
  ])
  const transactions = [
    ...pendingTransactions.map((transaction) => ({
      transaction,
      status: 'pending' as const,
    })),
    ...confirmedTransactions.map((transaction) => ({
      transaction,
      status: 'confirmed' as const,
    })),
  ]
  const usedTransactionIds = new Set<string>()

  for (const { transaction, status } of transactions) {
    const decimals = transaction.token_info?.decimals ?? 6
    const expectedValue = decimalToUnits(amount, decimals)
    if (expectedValue === null) return null

    const transactionValue = transaction.value && /^\d+$/.test(transaction.value)
      ? BigInt(transaction.value)
      : null
    if (
      transaction.transaction_id &&
      !usedTransactionIds.has(transaction.transaction_id) &&
      normalizeAddress(transaction.to) === address &&
      transactionValue === expectedValue &&
      (status === 'pending' || (transaction.block_timestamp || 0) >= minTimestamp)
    ) {
      usedTransactionIds.add(transaction.transaction_id)
      return {
        transactionId: transaction.transaction_id,
        amount,
        blockTimestamp: transaction.block_timestamp,
        status,
      }
    }
  }

  return null
}

const fetchRecentUsdtTransactions = async (
  address: string,
  signal: AbortSignal,
): Promise<RecentUsdtTransaction[]> => {
  const [pendingTransactions, confirmedTransactions] = await Promise.all([
    fetchTronGridUsdtTransactions(address, signal, {
      limit: 3,
      status: 'pending',
    }),
    fetchTronGridUsdtTransactions(address, signal, {
      limit: 3,
      status: 'confirmed',
    }),
  ])
  const usedTransactionIds = new Set<string>()
  const transactions = [
    ...pendingTransactions.map((transaction) => ({
      transaction,
      status: 'pending' as const,
    })),
    ...confirmedTransactions.map((transaction) => ({
      transaction,
      status: 'confirmed' as const,
    })),
  ]

  return transactions
    .filter(({ transaction }) => {
      if (
        !transaction.transaction_id ||
        usedTransactionIds.has(transaction.transaction_id) ||
        !transaction.value ||
        !/^\d+$/.test(transaction.value) ||
        normalizeAddress(transaction.to) !== address
      ) {
        return false
      }

      usedTransactionIds.add(transaction.transaction_id)
      return true
    })
    .slice(0, 3)
    .map(({ transaction, status }) => ({
      transactionId: transaction.transaction_id,
      amount: unitsToDecimal(transaction.value || '0', transaction.token_info?.decimals ?? 6),
      blockTimestamp: transaction.block_timestamp,
      status,
    }))
}

export function RechargeDialog({ open, onOpenChange, accountInfo, onSuccess }: RechargeDialogProps) {
  const [step, setStep] = useState(1)
  const [rechargeTypes, setRechargeTypes] = useState<RechargeType[]>([])
  const [channelInfo, setChannelInfo] = useState<ChannelInfo | null>(null)
  const [selectedAmount, setSelectedAmount] = useState('')
  const [usdtList, setUsdtList] = useState<UsdtInfo[]>([])
  const [selectedUsdtIndex, setSelectedUsdtIndex] = useState(0)
  const [transactionId, setTransactionId] = useState('')
  const [rechargeNumber, setRechargeNumber] = useState('')
  const [matchedTransaction, setMatchedTransaction] = useState<MatchedUsdtTransaction | null>(null)
  const [monitorError, setMonitorError] = useState('')
  const [monitoring, setMonitoring] = useState(false)
  const [recentTransactions, setRecentTransactions] = useState<RecentUsdtTransaction[]>([])
  const [recentTransactionsLoading, setRecentTransactionsLoading] = useState(false)
  const [recentTransactionsError, setRecentTransactionsError] = useState('')
  const monitorAbortRef = useRef<AbortController | null>(null)
  const recentTransactionsAbortRef = useRef<AbortController | null>(null)

  const selectedUsdt = usdtList[selectedUsdtIndex]
  const rechargeAddress = normalizeAddress(selectedUsdt?.recharge_address)
  const normalizedRechargeNumber = normalizeAmount(rechargeNumber)
  const canMonitorUsdtAddress =
    step === 3 &&
    Boolean(rechargeAddress) &&
    Boolean(normalizedRechargeNumber) &&
    isPositiveAmount(normalizedRechargeNumber)
  const matchedTransactionTime = formatMonitorTime(matchedTransaction?.blockTimestamp)
  const duplicatedRecentTransaction = recentTransactions.find(
    (transaction) => normalizeAmount(transaction.amount) === normalizedRechargeNumber,
  )

  const resetDialog = useCallback(() => {
    setStep(1)
    setChannelInfo(null)
    setSelectedAmount('')
    setUsdtList([])
    setSelectedUsdtIndex(0)
    setTransactionId('')
    setRechargeNumber('')
    setMatchedTransaction(null)
    setMonitorError('')
    setMonitoring(false)
    setRecentTransactions([])
    setRecentTransactionsError('')
    setRecentTransactionsLoading(false)
    monitorAbortRef.current?.abort()
    monitorAbortRef.current = null
    recentTransactionsAbortRef.current?.abort()
    recentTransactionsAbortRef.current = null
  }, [])

  // Step 1: 加载充值方式列表
  const loadRechargeTypes = useCallback(async () => {
    if (!accountInfo.plat || !accountInfo.remarks_name) {
      toast.error('账号信息缺失')
      return
    }

    try {
      const resp = await requestMobile('/common/apis/v1/typeList', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })
      if (resp && resp.data) {
        setRechargeTypes(resp.data)
        setStep(1)
      }
    } catch (error) {
      console.error('Failed to load recharge types:', error)
      toast.error('加载充值方式失败')
    }
  }, [accountInfo.plat, accountInfo.remarks_name])

  // 当对话框打开时加载充值方式列表
  useEffect(() => {
    if (open) {
      loadRechargeTypes()
    } else {
      resetDialog()
    }
  }, [loadRechargeTypes, open, resetDialog])

  useEffect(() => {
    setMatchedTransaction(null)
    setMonitorError('')
  }, [rechargeAddress, normalizedRechargeNumber])

  useEffect(() => {
    setRecentTransactions([])
    setRecentTransactionsError('')

    if (!open || step !== 3 || !rechargeAddress) {
      setRecentTransactionsLoading(false)
      recentTransactionsAbortRef.current?.abort()
      recentTransactionsAbortRef.current = null
      return
    }

    let stopped = false
    const controller = new AbortController()
    recentTransactionsAbortRef.current?.abort()
    recentTransactionsAbortRef.current = controller
    setRecentTransactionsLoading(true)

    fetchRecentUsdtTransactions(rechargeAddress, controller.signal)
      .then((transactions) => {
        if (stopped) return
        setRecentTransactions(transactions)
        setRecentTransactionsError('')
      })
      .catch((error) => {
        if (stopped || controller.signal.aborted) return
        console.error('Failed to load recent USDT transactions:', error)
        setRecentTransactionsError('最近交易记录加载失败')
      })
      .finally(() => {
        if (stopped) return
        setRecentTransactionsLoading(false)
        if (recentTransactionsAbortRef.current === controller) {
          recentTransactionsAbortRef.current = null
        }
      })

    return () => {
      stopped = true
      controller.abort()
      if (recentTransactionsAbortRef.current === controller) {
        recentTransactionsAbortRef.current = null
      }
    }
  }, [open, rechargeAddress, step])

  useEffect(() => {
    if (!open || !canMonitorUsdtAddress || matchedTransaction) {
      setMonitoring(false)
      monitorAbortRef.current?.abort()
      monitorAbortRef.current = null
      return
    }

    let stopped = false

    const poll = async () => {
      monitorAbortRef.current?.abort()
      const controller = new AbortController()
      monitorAbortRef.current = controller
      setMonitoring(true)

      try {
        const matched = await findMatchingUsdtTransaction(
          rechargeAddress,
          normalizedRechargeNumber,
          controller.signal,
        )
        if (stopped) return

        if (matched) {
          setMatchedTransaction(matched)
          setMonitoring(false)
          setMonitorError('')
          return
        }

        setMonitorError('')
      } catch (error) {
        if (stopped || controller.signal.aborted) return
        console.error('Failed to monitor USDT recharge:', error)
        setMonitorError('监控请求失败，正在重试')
      } finally {
        if (monitorAbortRef.current === controller) {
          monitorAbortRef.current = null
        }
      }
    }

    poll()
    const timer = window.setInterval(poll, TRONGRID_POLL_INTERVAL)

    return () => {
      stopped = true
      window.clearInterval(timer)
      monitorAbortRef.current?.abort()
      monitorAbortRef.current = null
    }
  }, [canMonitorUsdtAddress, matchedTransaction, normalizedRechargeNumber, open, rechargeAddress])

  // Step 2: 选择充值方式后，加载金额或USDT列表
  const handleTypeSelect = async (type: RechargeType) => {
    if (!isSupportedUsdtRechargeType(type)) {
      toast.error('当前 USDT 充值方式不支持')
      return
    }

    if (isUsdtRechargeType(type) && !isNormalUsdtRechargeType(type)) {
      // USDT 链上充值（type=4）
      try {
        const resp = await requestMobile('/common/apis/v1/usdt-list', {
          plat: accountInfo.plat,
          name: accountInfo.remarks_name
        })
        if (resp && resp.data?.data) {
          setUsdtList(resp.data.data)
          setStep(3)
        }
      } catch (error) {
        console.error('Failed to load USDT list:', error)
        toast.error('加载USDT列表失败')
      }
    } else {
      // 普通充值
      try {
        const resp = await requestMobile('/common/apis/v1/channelList', {
          plat: accountInfo.plat,
          name: accountInfo.remarks_name,
          typeId: type.id
        })
        if (resp && resp.data?.length) {
          setChannelInfo(resp.data[0])
          setStep(2)
        }
      } catch (error) {
        console.error('Failed to load channel info:', error)
        toast.error('加载充值渠道失败')
      }
    }
  }

  // Step 2: 提交普通充值
  const handleRecharge = async () => {
    if (!selectedAmount) {
      toast.error('请选择充值金额')
      return
    }

    const winOpen = window.open('', '_blank')
    try {
      const resp = await requestMobile('/common/apis/v1/recharge', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        payChannelId: channelInfo?.id,
        money: selectedAmount
      })
      if (resp && resp.data && winOpen) {
        winOpen.location.href = resp.data
        onOpenChange(false)
        resetDialog()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Failed to recharge:', error)
      toast.error('充值失败')
      winOpen?.close()
    }
  }

  // Step 3: 提交USDT充值
  const handleUsdtRecharge = async () => {
    if (!transactionId || !rechargeNumber) {
      toast.error('请填写完整信息')
      return
    }

    try {
      const selectedUsdt = usdtList[selectedUsdtIndex]
      const resp = await requestMobile('/common/apis/v1/usdt-recharge', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        id: selectedUsdt.id,
        transaction_id: transactionId,
        recharge_number: rechargeNumber
      })
      if (resp && resp.code === 0) {
        toast.success('提交成功')
        onOpenChange(false)
        resetDialog()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Failed to submit USDT recharge:', error)
      toast.error('提交失败')
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('复制成功')
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('复制失败')
    }
  }

  const amountList = channelInfo?.quickAmount
    ? channelInfo.quickAmount.split(',')
    : []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>充值</DialogTitle>
        </DialogHeader>

        {/* Step 1: 选择充值方式 */}
        {step === 1 && (
          <div className="space-y-2">
            {rechargeTypes.map((type) => {
              const unsupportedUsdt = !isSupportedUsdtRechargeType(type)
              return (
                <div
                  key={type.id}
                  className={`p-4 border rounded-lg ${
                    unsupportedUsdt
                      ? 'cursor-not-allowed border-red-500/50 bg-red-500/10 text-red-600'
                      : 'cursor-pointer hover:bg-muted'
                  }`}
                  onClick={() => {
                    if (!unsupportedUsdt) {
                      handleTypeSelect(type)
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{type.name}</div>
                    {unsupportedUsdt && (
                      <span className="text-xs font-medium text-red-600">
                        不支持
                      </span>
                    )}
                  </div>
                  {(type.tex1 || type.tex2) && (
                    <div
                      className={`text-sm mt-1 ${
                        unsupportedUsdt ? 'text-red-500' : 'text-muted-foreground'
                      }`}
                    >
                      {type.tex1 || type.tex2}
                    </div>
                  )}
                  {unsupportedUsdt && (
                    <div className="text-xs text-red-500 mt-2">
                      USDT 充值仅支持 type 为 1（正常充值）或 4（链上充值）的方式
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Step 2: 选择充值金额 */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>选择金额</Label>
              <RadioGroup value={selectedAmount} onValueChange={setSelectedAmount}>
                <div className="grid grid-cols-3 gap-2">
                  {amountList.map((amount) => (
                    <div key={amount} className="relative">
                      <RadioGroupItem
                        value={amount}
                        id={`amount-${amount}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`amount-${amount}`}
                        className="flex items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                      >
                        ¥{amount}
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                返回
              </Button>
              <Button onClick={handleRecharge} disabled={!selectedAmount} className="flex-1">
                去充值
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: USDT充值 */}
        {step === 3 && (
          <div className="space-y-4">
            {/* USDT链选择 */}
            {usdtList.length > 1 && (
              <div className="space-y-2">
                <Label>选择链</Label>
                <RadioGroup
                  value={selectedUsdtIndex.toString()}
                  onValueChange={(v) => setSelectedUsdtIndex(parseInt(v))}
                >
                  {usdtList.map((usdt, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <RadioGroupItem value={index.toString()} id={`usdt-${index}`} />
                      <Label htmlFor={`usdt-${index}`}>{usdt.chain_name}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* 当前选中的USDT信息 */}
            {selectedUsdt && (
              <div className="space-y-3 p-4 bg-muted rounded-lg">
                <div>
                  <div className="text-sm text-muted-foreground">链名称</div>
                  <div className="font-medium">{selectedUsdt.channel_name}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">充值地址</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-sm break-all font-mono">
                      {selectedUsdt.recharge_address}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(selectedUsdt.recharge_address)}
                    >
                      复制
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 最近USDT交易记录 */}
            {selectedUsdt && (
              <div className="space-y-1.5 rounded-lg border p-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs">最近 USDT 交易</Label>
                  {recentTransactionsLoading && (
                    <span className="text-xs text-muted-foreground">加载中...</span>
                  )}
                </div>
                {recentTransactionsError && (
                  <div className="text-xs text-red-500">{recentTransactionsError}</div>
                )}
                {!recentTransactionsLoading && !recentTransactionsError && recentTransactions.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    暂无最近交易记录
                  </div>
                )}
                {recentTransactions.length > 0 && (
                  <div className="space-y-1">
                    {recentTransactions.map((transaction) => (
                      <div
                        key={transaction.transactionId}
                        className="rounded-md bg-muted/60 px-2 py-1.5 text-xs"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{transaction.amount} USDT</span>
                          <span
                            className={`shrink-0 ${
                              transaction.status === 'pending'
                                ? 'text-amber-600'
                                : 'text-green-600'
                            }`}
                          >
                            {formatTransactionStatus(transaction.status)}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between gap-2 text-muted-foreground">
                          <span>{formatMonitorTime(transaction.blockTimestamp) || '-'}</span>
                          <span className="font-mono">
                            {formatTransactionId(transaction.transactionId)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 充值表单 */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="transaction_id">交易ID</Label>
                <Input
                  id="transaction_id"
                  placeholder="请输入交易ID"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recharge_number">充值数量</Label>
                <Input
                  id="recharge_number"
                  type="number"
                  placeholder="请输入充值数量"
                  value={rechargeNumber}
                  onChange={(e) => setRechargeNumber(e.target.value)}
                />
                {duplicatedRecentTransaction && (
                  <p className="text-xs text-red-500">
                    最近交易中已有相同数量：{duplicatedRecentTransaction.amount} USDT
                    （{formatTransactionStatus(duplicatedRecentTransaction.status)}）。
                    请确认不要重复填写。
                  </p>
                )}
                {canMonitorUsdtAddress && (
                  <div className="space-y-1">
                    <Button
                      type="button"
                      variant={matchedTransaction ? 'default' : 'outline'}
                      disabled={!matchedTransaction}
                      onClick={() => {
                        if (!matchedTransaction) return
                        setTransactionId(matchedTransaction.transactionId)
                        toast.success('已填充交易ID')
                      }}
                      className="w-full"
                    >
                      {matchedTransaction
                        ? `已发现${formatTransactionStatus(matchedTransaction.status)}交易，点击填充${matchedTransactionTime ? `（${matchedTransactionTime}）` : ''}`
                        : monitoring
                          ? (
                            <span className="flex items-center justify-center gap-2">
                              <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              正在监控 {normalizedRechargeNumber} USDT
                            </span>
                          )
                          : '等待监控启动...'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      自动检查最近 5 分钟内，充值地址是否出现相同数量的待确认或已确认 USDT。
                    </p>
                    {monitorError && (
                      <p className="text-xs text-red-500">{monitorError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                返回
              </Button>
              <Button onClick={handleUsdtRecharge} className="flex-1">
                提交充值
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
